import _ from 'lodash';

export default function(ngModule) {
    ngModule.factory('Event', function(BaseClass, $q, EventDate, Ticket, Product, $http, ScannerInstance, Locales,
        Currencies, $timeout, RelationAPI, $state, Shop, $translate, ScannerType, generateRandomString, MetaData, Company) {

        /**
         * Constructor function
         *
         * @constructor
         * @param {Object} attributes Initial attributes for the model
         */
        function Event(attributes) {
            _.assign(this,attributes);
        }
        Event.modelName = 'Event';


        BaseClass.apply(Event);
        Event.configureApi('/event/:guid',{
            queryAdmin: '/event/:status/:company',
            reinstate: '/event/:guid/reinstate' ,
        });
        Event.hasMany(
            'EventDate',         // Modelname
            'event_dates',       // propertyName
            'event_id',          // foreign key
            '/event/:guid/dates',// URL
            null,                // fnSuffix
            function(dates) {    // Success Function
                return _.filter(dates, d => d.name !== 'Proxy date');
            }
        );
        Event.belongsToMany('Ticket','tickets','/event/:guid/tickets', [{
            type: 'query',
            fn: function(modelName, url, instance, cacheOnly = false){
                if(cacheOnly)
                    return $q.resolve(instance.belongsToMany.restore(modelName));
                // Override query function to skip caching unless explicitly instructed.
                // We can't reliably cache this relationship because its through
                // Product->EventDate#event_id. So instead always hit the API
                return $http.get(url).then(response => {
                    instance.broadcastAPIMessage(response);
                    var models = _.map(response.data, m => {
                        m = Ticket.new(m);
                        instance.belongsToMany.cache(modelName, m);
                        return m;
                    });
                    return $q.resolve(models);
                }, error => {
                    instance.broadcastAPIMessage(error);
                    return $q.reject(error);
                });
            }
        }]);
        Event.belongsToMany('Product','products','/event/:guid/products',[{
            type: 'query',
            fn: function(modelName, url, instance, cacheOnly = false){
                if(cacheOnly)
                    return $q.resolve(instance.belongsToMany.restore(modelName));
                // override query function to skip caching. We can't reliably cache this relationship because its
                // through EventDate#event_id. So instead always hit the API
                return $http.get(url).then(response => {
                    instance.broadcastAPIMessage(response);
                    var models = _.map(response.data, m => {
                        m = Product.new(m);
                        instance.belongsToMany.cache(modelName, m);
                        return m;
                    });
                    return $q.resolve(models);
                }, error => {
                    instance.broadcastAPIMessage(error);
                    return $q.reject(error);
                });
            }
        }]);
        Event.prototype.toString = function() { return this.name; };

        Event.validates({
            name: {
                required: true
            },
            website: {
                regex: {
                    regex: /^https?:\/\/(?:[\w\d-]+\.)+\w{2,}/i,
                    message: 'geen geldige url'
                }
            },
            type: {
                required: true,
                inclusion: {
                    in: ['once','repeating']
                }
            },
            locale: {
                required: true,
                inclusion: {
                    in: _.map(Locales, 'code')
                }
            },
            currency: {
                required: true,
                inclusion: {
                    in: _.map(Currencies, 'code')
                }
            }
        });

        /**
         * Figure out if we're dealing with legacy event, can't apply the 1-date-1-product workflow to legacy events
         *
         * Queries dates->products for event, and checks whether their setup matches new workflow
         *
         * @returns {Boolean} Promise that resolves with true if event supports simple ticket, false otherwise
         */
        Event.prototype.$supportsSimpleTicket = function() {
            return this.$queryEventDate().then(dates => {
                let promises = _.map(dates, date =>{
                    return date.$queryProduct().then(products => {
                        return [date.guid, products];
                    });
                });
                return $q.all(promises).then(unpairedProducts => _.fromPairs(unpairedProducts));
            }).then(productsPerDate => {
                let guidRegex = /^[\w]{8}(-[\w]{4}){3}-[\w]{12}$/;
                let incompatible = _.findKey(productsPerDate, products => {
                    if (products.length > 1) return true;
                    if (products.length === 0) return false;
                    let product = products[0];
                    return product.name.indexOf('Access for') < 0 || !guidRegex.test(product.description);
                });
                return _.isUndefined(incompatible);
            });
        };

        /**
         * Query the event for Tickets WITH nested shops
         *
         * @returns {Promise<Array>} Resolves with an array of scanners
         */
        Event.prototype.$queryTicketsWithShops = function() {
            return $http.get(`/event/${ this.guid }/ticketswithshops`).then(response => {
                this.broadcastAPIMessage(response);

                return $q.resolve(_.map(response.data, ticketData => {
                    let ticket = Ticket.new(ticketData);

                    ticket.shops = _.map(ticketData.shops, shopData => Shop.new(shopData));

                    return ticket;
                }));
            }, error => {
                this.broadcastAPIMessage(error);

                return $q.reject(error);
            });
        };

        /**
         * Query the event for scannerTypes
         *
         * @returns {Promise<Array>} Resolves with an array of scannerTypes
         */
        Event.prototype.$queryScannerTypes = function() {
            return $http.get(`/event/${ this.guid }/scannertypes`).then(response => {
                this.broadcastAPIMessage(response);

                return $q.resolve(_.map(response.data, data => {
                    return ScannerType.new(data);
                }));
            }, error => {
                this.broadcastAPIMessage(error);

                return $q.reject(error);
            });
        };

        /**
         * Query the event for active scanners
         *
         * @returns {Promise<Array>} Resolves with an array of scanners
         */
        Event.prototype.$queryScanners = function() {
            return $http.get(`/event/${ this.guid }/scanners`)
                .then(res => _.map(res.data, ScannerInstance.new));
        };

        /**
         * Query the event for scanner types
         *
         * @returns {Promise<Array>} Resolves with an array of scanner types
         */
        Event.prototype.$queryEventScanner = function() {
            var scannerType;
            return ScannerType.query().then(_scannerTypes => {
                scannerType = _scannerTypes;
                return RelationAPI.get('EventScanner', this.guid);
            }).then(relations => {
                return $q.all(_.map(relations, relation => {
                    const cached = _.first(ScannerType.cached.where({ guid: relation.right }));
                    if(cached)
                        return $q.resolve(cached);
                    return RelationAPI.delete('EventScanner', relation.left, relation.right)
                        .then(() => null);
                }));
            }).then(scannerTypes => _.compact(scannerTypes));
        };

        /**
         * Query the event for scanner types, creates one if it doesn't exist yet
         *
         * @returns {Promise<Array>} Resolves with an array of scanner types
         */
        Event.prototype.$queryOrCreateEventScanner = function() {
            var scannerType;
            return this.$queryEventScanner()
                .then(scanners => {
                    if(scanners.length > 0)
                        return scanners;
                    return ScannerType.create({
                        username: generateRandomString(6),
                        password: generateRandomString(4),
                        name: [this.name, $translate.instant('models.models.scannerType', { count: 2 })].join(' - '),
                        expires_at: moment().add(1, 'year').startOf('day').format('YYYY-MM-DDTHH:mm:ssZ')
                    }).then(scanner => RelationAPI.create('EventScanner', this.guid, scanner.guid).then(() => [scanner]));
                });
        };

        Event.prototype.$queryAllOrNewScanners = function() {
            return this.$queryOrCreateEventScanner().then(scanners => {
                if(!_.isArray(scanners))
                    scanners = [scanners];

                return this.$queryScannerTypes().then(allEventScanners => {
                    return _.unionBy(scanners, allEventScanners, 'guid');
                })
            })
        };

        /**
         * Query the relations API for products that belong to an event, but don't have an EventDate
         *
         * @returns {Promise<Array>} Resolves with an array of products
         */
        Event.prototype.$queryAddonProduct = function() {
            let relations;
            return RelationAPI.get('AddonProduct', this.guid)
                .then(_relations => {
                    relations = _relations;
                    if(!relations.length)
                        return [];
                    return Product.query();
                })
                .then(products => {
                    const addonProducts = _.filter(products, product => {
                        return _.some(relations, { right: product.guid });
                    });
                    let brokenRelations = _.filter(relations, relation => {
                        return !_.some(addonProducts, { guid: relation.right });
                    });
                    return $q.all(brokenRelations, relation => {
                        return RelationAPI.delete('AddonProduct', relation.left, relation.right);
                    }).then(() => addonProducts);
                });
        };

        /**
         * Query the relations API for Tickets that belong to an event, but don't have an AccessProduct
         *
         * @returns {Promise<Array>} Resolves with an array of tickets
         */
        Event.prototype.$queryAddonTicket = function() {
            let relations;
            return RelationAPI.get('AddonTicket', this.guid)
                .then(_relations => {
                    relations = _relations;
                    if(!relations.length)
                        return [];
                    return Ticket.query();
                })
                .then(tickets => {
                    const addonTickets = _.filter(tickets, ticket => {
                        return _.some(relations, { right: ticket.guid });
                    });
                    let brokenRelations = _.filter(relations, relation => {
                        return !_.some(addonTickets, { guid: relation.right });
                    });
                    return $q.all(brokenRelations, relation => {
                        return RelationAPI.delete('AddonTicket', relation.left, relation.right);
                    }).then(() => addonTickets);
                });
        };

        /**
         * Find this Event's shops
         *
         * @return {Promise<Shop[]>} Finds an event shop(s)
         */
        Event.prototype.$queryShop = function() {
            return $http.get(`/event/${this.guid}/shops`)
                .then(response => {
                    return response.data.map(shop => Shop.new(shop))
                }, error => {
                    return $q.reject(error)
                })
        };

        /**
         * Queries or creates this Event's shop
         *
         * @return {Promise<Shop[]>} Resolves with one or more shops
         */
        Event.prototype.$queryOrCreateShop = function() {
            let event = this;
            return event.$queryShop().then(shops => {
                if(shops.length)
                    return $q.resolve(shops);
                return Shop.create({
                    name: event.name + ' - ' + $translate.instant('models.models.shop', { count: 1 }),
                }).then(shop => {
                    return RelationAPI.create('EventShop', event.guid, shop.guid)
                        .then(() => MetaData.query())
                        .then(meta => {
                            var defaults = _.filter(meta, m => {
                                return m.public && /^(age|city|gender)$/.test(m.name);
                            });
                            return _.reduce(
                                defaults,
                                (promise, meta) => promise.then(() => shop.$attachMetaData(meta)),
                                $q.resolve()
                            );
                        })
                        .then(() => [shop]);
                });
            });
        };

        /**
         * Search for an event (Admin feature)
         *
         * @param {String} query A search query
         * @return {Promise<Event[]>} Promise that resolves with an array of events
         */
        Event.search = function(query) {
            if(!_.isString(query) || _.isEmpty(query)) return $q.reject('invalid query');
            return $http.post('/statistics/events/search', { search: query }).then(response => {
                let events = response.data
                _.forEach(events, function(event){
                    let minDate = _.minBy(event.event_dates, d => moment(d.start).format('x'));
                    event.start = _.get(minDate, 'start' , 'no date');
                });
                let upcoming = [];
                let past = [];
                let other = [];
                _.forEach(events, function(event){
                    if(event.start === 'no date')
                        other.push(event);
                    else if (moment().diff(event.start, 'minutes') < 0)
                        upcoming.push(event);
                    else if (moment().diff(event.start, 'minutes') >= 0)
                        past.push(event);
                });

                upcoming = _.orderBy(upcoming, ['start'],['asc']);
                past = _.orderBy(past, ['start'],['desc']);

                let upcomingHeader = {name: '-------------- Upcoming --------------', start : '', company: {name: ''}};
                let pastHeader = {name: '-------------- Past --------------', start : '', company: {name: ''}};
                let otherHeader = {name: '-------------- Other --------------', start : '', company: {name: ''}};
                let result = [upcomingHeader].concat(upcoming, [pastHeader], past, [otherHeader], other);
                return result;
            });
        };

        return Event;
    });
}
